/**
 * Where marked puts a task item's checkbox: first in the `li`, or first in the
 * `li`'s first paragraph (a loose list). A checkbox anywhere else is not a task
 * item's, so it is left for the sanitizer to strip.
 */
const isTaskCheckbox = (input: Element): boolean => {
  const parent = input.parentElement;
  if (parent?.firstChild !== input) {
    return false;
  }
  if (parent.tagName === 'LI') {
    return true;
  }
  const item = parent.parentElement;
  return (
    parent.tagName === 'P' &&
    item?.tagName === 'LI' &&
    item.firstChild === parent
  );
};

const taskCheckbox = (checked: boolean): HTMLSpanElement => {
  const box = document.createElement('span');
  box.className = 'task-checkbox';
  box.setAttribute('role', 'checkbox');
  box.setAttribute('aria-checked', String(checked));
  box.setAttribute('aria-disabled', 'true');
  const glyph = document.createElement('span');
  glyph.setAttribute('aria-hidden', 'true');
  glyph.textContent = checked ? '☑' : '☐';
  box.append(glyph);
  return box;
};

/**
 * Renders GFM task-list items (`- [ ] open`, `- [x] done`) as checkboxes that
 * survive Angular's HTML sanitizer (SB-3281).
 *
 * marked's own checkbox is `<input disabled type="checkbox">`, and the app's
 * `SANITIZE` step (`connectorSafeMarkdown`) passes every render through
 * `DomSanitizer.sanitize(SecurityContext.HTML, …)`. `input` is not on that
 * sanitizer's element allowlist, so it was stripped and the item fell back to an
 * ordinary bullet — while Copy's `text/plain` still carried the `- [ ]`, which
 * is how the bug surfaced. Each task checkbox is swapped for markup that is all
 * on the allowlist (`span`; `class`, `role`, `aria-*`) — `data-*` is not, which
 * is why the checked state lives in `aria-checked` and the CSS keys off it.
 *
 * This rewrites marked's HTML rather than overriding marked's renderer because
 * the chart recognizer needs marked's global instance stock: the guard beside
 * this file, `marked-instance.guard.spec.ts`, refuses any marked extension.
 * `connectorSafeMarkdown` calls this just before sanitizing, the one step
 * ngx-markdown runs after marked that cannot reach the lexer.
 *
 * The sanitizer stays on: model output is untrusted, so the fix changes what
 * reaches the sanitizer, never what it accepts. Only the checked state carries
 * over from the `<input>`; every attribute on the span is written here.
 *
 * The inner ☐/☑ glyph is for rich-text paste only. Copy's `text/html` goes
 * through this same `SANITIZE` step, and app CSS does not travel through the
 * clipboard, so the glyph is what keeps the box in Docs, Word or an email.
 * `styles.css` hides it in the app, where the box is drawn instead, and
 * `aria-hidden` keeps a screen reader from reading "ballot box" on top of the
 * checkbox role.
 */
export const withTaskListCheckboxes = (html: string): string => {
  // Most renders hold no task list; skip the parse for them.
  if (!html.includes('checkbox')) {
    return html;
  }
  const template = document.createElement('template');
  template.innerHTML = html;
  const inputs = Array.from(
    template.content.querySelectorAll('input[type="checkbox"]'),
  ).filter(isTaskCheckbox);
  if (inputs.length === 0) {
    return html;
  }
  inputs.forEach((input) => {
    input.closest('li')?.classList.add('task-list-item');
    input.replaceWith(taskCheckbox(input.hasAttribute('checked')));
  });
  return template.innerHTML;
};
